import type { IDropdownOption } from '@fluentui/react';
import { Stack } from '@fluentui/react';
import { Dropdown } from '@h2oai/ui-kit';
import { memo, useCallback, useMemo } from 'react';

import type { Tag } from '../../../../authz/gen/ai/h2o/workspace/v1/tag_pb';
import type { PageFilterProps } from '../../../../components/PageFilters/types';

export const tagsFilterKey = 'workspace-tags-filter';

type TagsFilterProps = PageFilterProps<string[]> & {
  availableTags?: Tag[];
};

export const TagsFilter = memo((props: TagsFilterProps) => {
  const { onChange, value, availableTags } = props;

  const tagOptions = useMemo((): IDropdownOption[] => {
    if (!availableTags?.length) return [];
    return availableTags.map((tag) => ({
      key: tag.name!,
      text: tag.displayName || tag.name!,
    }));
  }, [availableTags]);

  const handleChange = useCallback(
    (_e: unknown, option?: IDropdownOption) => {
      if (!option?.key) return;

      const tagKey = option.key as string;
      const currentTags = value || [];
      const isSelected = currentTags.includes(tagKey);

      let newTags: string[];
      if (isSelected) {
        newTags = currentTags.filter((t) => t !== tagKey);
      } else {
        newTags = [...currentTags, tagKey];
      }

      const readableValue =
        newTags.length > 0
          ? newTags
              .map((tagName) => {
                const tag = availableTags?.find((t) => t.name === tagName);
                return tag?.displayName || tagName;
              })
              .join(', ')
          : '';

      onChange(newTags, readableValue);
    },
    [onChange, value, availableTags]
  );

  return (
    <Stack>
      <Dropdown
        styles={{
          root: {
            minWidth: 200,
            maxWidth: 400,
          },
        }}
        label="Filter by tags"
        multiSelect
        onChange={handleChange}
        options={tagOptions}
        selectedKeys={value || []}
        placeholder="All tags"
      />
    </Stack>
  );
});
